import { useEffect, useState, type FormEvent } from "react";
import { useI18n } from "../../i18n/localization";
import { useAdminSession } from "./AdminSessionGate";
import {
  createRegisteredUser, listRegisteredUsers, regUserErrorKey, removeRegisteredUser, removeRow, sortRegisteredUsers,
  updateRegisteredUser, upsertRow, validateRegisteredUserForm, type RegisteredUserForm, type RegisteredUserRow,
  type RegUserErrorKey
} from "./registeredUsersModel";

// Admin → Settings: registered users allowed to start the public phone flow. List, add, edit
// phone/display name, enable/disable, and safe removal (users with call history are
// deactivated instead of deleted — the server decides and reports the outcome).
export function RegisteredUsers() {
  const { t } = useI18n();
  const { session, guard } = useAdminSession();
  const [rows, setRows] = useState<RegisteredUserRow[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<RegUserErrorKey | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    guard(listRegisteredUsers(session.token))
      .then((r) => setRows(sortRegisteredUsers(r)))
      .catch((e: unknown) => setError(regUserErrorKey(e)));
  }, [session.token, guard]);

  /** Runs an admin action; resolves true when it completed with a success notice. */
  const act = async (work: () => Promise<string | null>): Promise<boolean> => {
    setError(null);
    setNotice(null);
    try {
      const done = await work();
      setNotice(done);
      return done != null;
    } catch (e) {
      setError(regUserErrorKey(e));
      return false;
    }
  };

  const add = (form: RegisteredUserForm) => act(async () => {
    const v = validateRegisteredUserForm(form);
    if (!v.ok) { setError(v.error); return null; }
    const row = await guard(createRegisteredUser(session.token, { phone: v.phone, displayName: v.displayName, isActive: true }));
    setRows((r) => upsertRow(r ?? [], row));
    return t.regUsers.saved;
  });

  const save = (id: number, form: RegisteredUserForm) => act(async () => {
    const v = validateRegisteredUserForm(form);
    if (!v.ok) { setError(v.error); return null; }
    const row = await guard(updateRegisteredUser(session.token, id, { phone: v.phone, displayName: v.displayName }));
    setRows((r) => upsertRow(r ?? [], row));
    setEditingId(null);
    return t.regUsers.saved;
  });

  const toggle = (u: RegisteredUserRow) => act(async () => {
    const row = await guard(updateRegisteredUser(session.token, u.id, { isActive: !u.isActive }));
    setRows((r) => upsertRow(r ?? [], row));
    return t.regUsers.saved;
  });

  const remove = (u: RegisteredUserRow) => act(async () => {
    if (!window.confirm(t.regUsers.removeConfirm)) return null;
    const r = await guard(removeRegisteredUser(session.token, u.id));
    if (r.outcome === "removed") {
      setRows((rs) => removeRow(rs ?? [], u.id));
      return t.regUsers.removed;
    }
    setRows((rs) => upsertRow(rs ?? [], { ...u, isActive: false }));
    return t.regUsers.deactivated;
  });

  return (
    <section aria-labelledby="admin-registered-users" style={card}>
      <h2 id="admin-registered-users" style={{ fontSize: 15, color: "#14305f", margin: "0 0 6px" }}>{t.regUsers.title}</h2>
      <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 14px" }}>{t.regUsers.hint}</p>

      <AddUserForm onAdd={add} />

      <div aria-live="polite" style={{ minHeight: 18, margin: "8px 0", fontSize: 13 }}>
        {error && <div role="alert" style={{ color: "#b91c1c" }}>{t.regUsers.errors[error]}</div>}
        {notice && !error && <div role="status" style={{ color: "#15803d" }}>{notice}</div>}
      </div>

      {rows == null ? (
        <p role="status" style={{ color: "#5b6b82", fontSize: 13 }}>{t.regUsers.loading}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "#5b6b82", fontSize: 13 }}>{t.regUsers.none}</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "start", color: "#64748b" }}>
                <th scope="col" style={th}>{t.regUsers.name}</th>
                <th scope="col" style={th}>{t.regUsers.phone}</th>
                <th scope="col" style={th}>{t.regUsers.status}</th>
                <th scope="col" style={th}>{t.regUsers.actions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => editingId === u.id ? (
                <EditRow key={u.id} user={u} onSave={(f) => void save(u.id, f)} onCancel={() => setEditingId(null)} />
              ) : (
                <tr key={u.id} style={{ borderBottom: "1px solid #eef3fb" }}>
                  <td style={td}>{u.displayName}</td>
                  <td style={td}><span dir="ltr" style={{ unicodeBidi: "isolate" }}>{u.phoneDisplay}</span></td>
                  <td style={{ ...td, color: u.isActive ? "#15803d" : "#64748b" }}>{u.isActive ? t.regUsers.active : t.regUsers.disabled}</td>
                  <td style={{ ...td, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button type="button" style={smallBtn} onClick={() => { setEditingId(u.id); setError(null); }}
                      aria-label={`${t.regUsers.edit}: ${u.displayName}`}>{t.regUsers.edit}</button>
                    <button type="button" style={smallBtn} onClick={() => void toggle(u)}
                      aria-label={`${u.isActive ? t.regUsers.disable : t.regUsers.enable}: ${u.displayName}`}>
                      {u.isActive ? t.regUsers.disable : t.regUsers.enable}
                    </button>
                    <button type="button" style={{ ...smallBtn, color: "#b91c1c" }} onClick={() => void remove(u)}
                      aria-label={`${t.regUsers.remove}: ${u.displayName}`}>{t.regUsers.remove}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AddUserForm({ onAdd }: { onAdd: (f: RegisteredUserForm) => Promise<boolean> }) {
  const { t } = useI18n();
  const [phone, setPhone] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    if (await onAdd({ phone, displayName })) { setPhone(""); setDisplayName(""); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} noValidate style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
      <label style={fieldLabel}>
        {t.regUsers.name}
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={128} required style={input} />
      </label>
      <label style={fieldLabel}>
        {t.regUsers.phone}
        <input type="tel" inputMode="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} required style={input} />
      </label>
      <button type="submit" disabled={busy} aria-busy={busy} style={primaryBtn}>{t.regUsers.add}</button>
    </form>
  );
}

function EditRow({ user, onSave, onCancel }: { user: RegisteredUserRow; onSave: (f: RegisteredUserForm) => void; onCancel: () => void }) {
  const { t } = useI18n();
  const [phone, setPhone] = useState(user.phoneDisplay);
  const [displayName, setDisplayName] = useState(user.displayName);
  return (
    <tr style={{ borderBottom: "1px solid #eef3fb", background: "#f8fafc" }}>
      <td style={td}>
        <input aria-label={t.regUsers.name} value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={128} style={input} />
      </td>
      <td style={td}>
        <input aria-label={t.regUsers.phone} type="tel" inputMode="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} style={input} />
      </td>
      <td style={td}>{user.isActive ? t.regUsers.active : t.regUsers.disabled}</td>
      <td style={{ ...td, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button type="button" style={primaryBtn} onClick={() => onSave({ phone, displayName })}>{t.regUsers.save}</button>
        <button type="button" style={smallBtn} onClick={onCancel}>{t.regUsers.cancel}</button>
      </td>
    </tr>
  );
}

const card: React.CSSProperties = { border: "1px solid #e2e8f0", borderRadius: 10, padding: 18, background: "#fff", marginBottom: 16 };
const th: React.CSSProperties = { padding: 6, fontWeight: 600 };
const td: React.CSSProperties = { padding: 6, verticalAlign: "middle" };
const fieldLabel: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, color: "#14305f", fontSize: 13, flex: "1 1 180px" };
const input: React.CSSProperties = { padding: "7px 10px", border: "1px solid #d6deea", borderRadius: 6, fontSize: 13, minWidth: 0, boxSizing: "border-box", width: "100%" };
const primaryBtn: React.CSSProperties = { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontSize: 13 };
const smallBtn: React.CSSProperties = { background: "#e2e8f0", color: "#1e2a3a", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 };
